import { Stripe, loadStripe as loadStripeJs } from '@stripe/stripe-js';
import { Session } from 'src/models/session/_types';
import { selectableItemTypes } from 'src/shared/constants/itemTypes';
import api from 'src/utils/ajax';
import { formatCurrency } from 'src/utils/numberFormatter';

import { Cart, CartItem, PaymentOption } from './_types';

export const loadStripe = async (session: Session): Promise<Stripe | null> => {
  if (!session.stripeToken) {
    return null;
  }

  return loadStripeJs(session.stripeToken, {
    apiVersion: '2024-12-18.acacia',
  });
};

export const addItem = async (cart: Cart, newItem: CartItem): Promise<Cart> => {
  const existingItemIndex = cart.cartItems.findIndex(
    (item) =>
      item.itemId === newItem.itemId && item.itemType === newItem.itemType
  );

  let updatedCartItems;
  if (existingItemIndex !== -1) {
    // Replace existing item
    updatedCartItems = [
      ...cart.cartItems.slice(0, existingItemIndex),
      newItem,
      ...cart.cartItems.slice(existingItemIndex + 1),
    ];
  } else {
    // Add new item
    updatedCartItems = [...cart.cartItems, newItem];
  }

  const updatedCart: Cart = {
    ...cart,
    cartItems: updatedCartItems,
  };

  return updatedCart;
};

export const removeItem = async (
  cart: Cart,
  itemToRemove: CartItem
): Promise<Cart> => {
  const updatedCartItems = cart.cartItems.filter(
    (item) =>
      !(
        item.itemId === itemToRemove.itemId &&
        item.itemType === itemToRemove.itemType
      )
  );

  const updatedCart: Cart = {
    ...cart,
    cartItems: updatedCartItems,
  };

  return updatedCart;
};

export const hasSubscriptionUpgrade = async (
  cart: Cart,
  purchasedItems: any[]
): Promise<boolean> => {
  const upgrades = await Promise.all(
    cart.cartItems.map(async (cartItem) => {
      return (
        !canBeMultiplePurchased(
          cartItem.itemId?.toString() || '',
          cartItem.itemType || ''
        ) &&
        (
          await hasPurchased(
            cartItem.itemId?.toString() || '',
            cartItem.itemType || '',
            purchasedItems
          )
        )?.id
      );
    })
  );

  return upgrades.some((upgrade) => upgrade);
};

export const validateItems = async (
  cart: Cart,
  purchasedItems: any[]
): Promise<any[]> => {
  const response = await api.post('/api/v4/carts/validate_items', { cart });
  const validatedItems = response.data;

  const removedItems: any[] = [];

  for (const item of validatedItems) {
    if (!item.valid) {
      const cartItem = cart.cartItems.find(
        (i) => i.itemId === item.item_id && i.itemType === item.item_type
      );
      if (cartItem) {
        cart = await removeItem(cart, cartItem);
        removedItems.push(item);
      }
    }
  }

  for (const cartItem of cart.cartItems) {
    if (
      !canBeMultiplePurchased(
        cartItem.itemId?.toString() || '',
        cartItem.itemType || ''
      )
    ) {
      const paymentOption = await hasPurchased(
        cartItem.itemId?.toString() || '',
        cartItem.itemType || '',
        purchasedItems
      );

      if (
        (paymentOption.id || paymentOption.amount) &&
        (paymentOption.type === 'purchase' ||
          cartItem.paymentOption?.type === 'purchase' ||
          paymentOption.isIap ||
          (paymentOption.id === cartItem.paymentOption?.id &&
            cartItem.paymentOption?.type === 'subscription'))
      ) {
        cart = await removeItem(cart, cartItem);
        removedItems.push({
          item_id: cartItem.itemId,
          item_type: cartItem.itemType,
          valid: false,
        });
      }
    }

    // TODO: Implement upsell validation
    // const hasUpgrade = await hasSubscriptionUpgrade(cart);
    // if (
    //   hasUpgrade &&
    //   cartItem.upsellId &&
    //   !removedItems.find(
    //     (i) => i.item_id === item.id && i.item_type === getItemType(item)
    //   )
    // ) {
    //   removedItems.push({
    //     item_id: item.id,
    //     item_type: getItemType(item),
    //     valid: false,
    //     reason: 'no_upsells',
    //   });
    // }
  }

  return removedItems;
};

export const cartTypeToItemType = (cartType?: string): string | undefined => {
  const itemType = selectableItemTypes.find(
    (itemType) => itemType.modelName === cartType
  );

  if (itemType) {
    return itemType.value;
  }

  return cartType;
};

const hasPurchased = async (
  itemId: string,
  itemType: string,
  purchasedItems: any[]
): Promise<any> => {
  return purchasedItems.find(
    (i) => i.item_id === itemId && i.item_type === itemType
  );
};

const canBeMultiplePurchased = (itemId: string, itemType: string): boolean => {
  return itemType === 'Fbm::Package' || itemType === 'Exercisecom::Product';
};

export const getPaymentOptionDetails = (
  paymentOption?: PaymentOption,
  includeName = true
): string => {
  if (!paymentOption) {
    return 'Free';
  }

  const details: string[] = [];

  const formatAmountFromCents = (amountInCents: number, currency: string) => {
    const amountInDollars = amountInCents / 100;
    return formatCurrency(amountInDollars, { currency });
  };

  const formattedAmount = formatAmountFromCents(
    paymentOption.amount,
    paymentOption.currency
  );

  if (paymentOption.type === 'purchase') {
    details.push(formattedAmount);
  } else if (paymentOption.type === 'subscription') {
    const intervalString =
      paymentOption.intervalCount === 1
        ? paymentOption.interval
        : `${paymentOption.intervalCount} ${paymentOption.interval}s`;
    details.push(`${formattedAmount}/${intervalString}`);

    if (paymentOption.name && includeName) {
      details.push(paymentOption.name);
    }

    if (paymentOption.trialPeriodDays) {
      details.push(`${paymentOption.trialPeriodDays} day trial`);
    }
  }

  if (paymentOption.isIap) {
    details.push('In-App Purchase');
  }

  if (paymentOption.location) {
    details.push(paymentOption.location);
  }

  return details.join(' • ');
};

export const isTerminalPaymentMethod = (cart: Cart) => {
  return (
    cart.paymentMethod &&
    typeof cart.paymentMethod === 'object' &&
    cart.paymentMethod.type === 'card_present'
  );
};
